import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';

import { ToastrService } from 'ngx-toastr';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Table } from '../../shared/table/table';
import { AppIcons } from '../../../core/icons/fa-icons';

import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { Setup } from '../../setup/shared/service/setup';
import { TableColumn, TableAction } from '../../shared/models/table-models';
import { Popup } from '../../../core/shared/components/popup/popup';

import { PickListService } from '../../../core/shared/services/picklist.service';
import { PICKLIST_GROUPS } from '../../../core/config/picklist.config';

import { take } from 'rxjs/operators';
import { BackButton } from "../../shared/buttons/back-button";
import { QUESTION_TYPE_KEY } from '../question-type.util';

@Component({
  selector: 'app-question-setup',
  standalone: true,
  imports: [CommonModule, Table, Popup, BackButton, TranslatePipe],
  templateUrl: './question-setup.html',
  styleUrl: './question-setup.css',
})
export class QuestionSetup implements OnInit {

  icons = AppIcons;

  list: any[] = [];
  columns: TableColumn[] = [];

  questionTypeMap: Record<number, string> = {};

  showPopup = false;
  isEditMode = false;
  formData: any = {};

  fields: DynamicFieldConfig[] = [];

  qstnrSeq: number | null = null;

  constructor(
    private setup: Setup,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private picklist: PickListService,
    private translate: TranslateService
  ) {}

  // ================= INIT =================
  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      this.qstnrSeq = id ? +id : null;

      this.loadPicklists();
    });
  }

  // ================= PICKLIST =================
  loadPicklists() {
    this.picklist.loadCodes([PICKLIST_GROUPS.QUESTION_TYPE]);

    this.picklist.picklists$
      .pipe(take(1))
      .subscribe((res: any) => {

        const list = res?.[PICKLIST_GROUPS.QUESTION_TYPE] || [];

        // build map
        const map: Record<number, string> = {};

        list.forEach((item: any) => {
          map[Number(item.refCdSeq)] = item.refCdDscr;
        });

        this.questionTypeMap = map;

        // rebuild UI AFTER map ready
        this.buildFields();
        this.buildColumns();

        this.loadData();
      });
  }

  // ================= FIELDS =================
  buildFields() {
    this.fields = [
      {
        key: 'qstSeq',
        label: 'QUESTIONNAIRE.ID',
        type: 'text',
        hidden: true,
      },
      {
        key: 'qstStr',
        label: 'QUESTIONNAIRE.QUESTION_SETUP.QUESTION_TEXT',
        type: 'textarea',
        validations: { required: true },
      },
      {
        key: 'qstTypKey',
        label: 'QUESTIONNAIRE.QUESTION_SETUP.QUESTION_TYPE',
        type: 'select',
        options$: this.picklist.getOptionsSync(PICKLIST_GROUPS.QUESTION_TYPE),
        validations: { required: true },
      },
      {
        key: 'qstStsKey',
        label: 'COMMON.STATUS',
        type: 'select',
        options$: [
          { label: 'COMMON.ACTIVE', value: 706 },
          { label: 'COMMON.INACTIVE', value: 707 },
        ],
      }
    ];
  }

  // ================= TABLE =================
  buildColumns() {
    this.columns = [
      { key: 'qstSeq', label: 'QUESTIONNAIRE.ID' },

      {
        key: 'qstStr',
        label: 'QUESTIONNAIRE.QUESTION_SETUP.QUESTION',
        type: 'link',
        onClick: (row: any) => this.goToAnswers(row)
      },

      {
        key: 'qstTypKey',
        label: 'COMMON.TYPE',
        type: 'map',
        map: this.questionTypeMap
      },

      {
        key: 'qstStsKey',
        label: 'COMMON.STATUS',
        type: 'badge',
        map: {
          706: 'COMMON.ACTIVE',
          707: 'COMMON.INACTIVE'
        },
        badgeMap: {
          706: 'status-active',
          707: 'status-inactive'
        }
      }
    ];
  }

  // ================= NAVIGATION =================
  goToAnswers(row: any) {
    if (Number(row.qstTypKey) === QUESTION_TYPE_KEY.INPUT) {
      this.toastr.info(this.translate.instant('QUESTIONNAIRE.QUESTION_SETUP.INPUT_NO_ANSWERS'));
      return;
    }

    this.router.navigate(['/answers', row.qstSeq], {
      queryParams: {
        type: row.qstTypKey,
        typeName: this.questionTypeMap[row.qstTypKey] || '',
      },
    });
  }

  // ================= LOAD DATA =================
  loadData() {

    if (!this.qstnrSeq) {
      this.list = [];
      return;
    }

    this.setup.getQuestionList(this.qstnrSeq).subscribe({
      next: (res: any[]) => {

        this.list = (res || []).map(item => ({
          qstSeq: item.qstSeq,
          qstStr: item.qstStr,
          qstTypKey: Number(item.qstTypKey),  // IMPORTANT
          qstStsKey: item.qstStsKey
        }));

      },
      error: () => {
        this.list = [];
      }
    });
  }

  // ================= POPUP =================
  openPopup(data?: any) {
    this.showPopup = true;

    if (data) {
      this.isEditMode = true;

      this.formData = {
        qstSeq: data.qstSeq,
        qstStr: data.qstStr,
        qstTypKey: data.qstTypKey,
        qstStsKey: data.qstStsKey,
      };
    } else {
      this.isEditMode = false;

      this.formData = {
        qstSeq: null,
        qstTypKey: 1,
        qstStsKey: 706
      };
    }
  }

  // ================= SAVE =================
  handleSave = (form: any) => {

    const payload = {
      qstSeq: form.qstSeq ?? null,
      qstnrSeq: this.qstnrSeq,

      qstStr: form.qstStr,
      qstTypKey: form.qstTypKey,
      qstStsKey: form.qstStsKey,

      delFlg: false
    };

    return this.setup.saveQuestion(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant(this.isEditMode ? 'QUESTIONNAIRE.UPDATED' : 'QUESTIONNAIRE.CREATED'));
        this.showPopup = false;
        this.loadData();
      },
      error: () => this.toastr.error(this.translate.instant('QUESTIONNAIRE.SAVE_FAILED'))
    });
  };

  // ================= ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.icons.edit,
      tooltip: 'COMMON.EDIT',
      class: 'action-edit',
      onClick: (row) => this.openPopup(row),
    }
  ];

  // ================= BACK =================

}